/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { orderBy } from "lodash";
import { yearToCropYearShort, OLAPTransformerResult } from "../../../shared";
import Highcharts, { SeriesOptionsType } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { CATEGORIES_AXIS, SERIES_AXIS } from "../constants";

type TransformData = {
  populationByYear: OLAPTransformerResult | null;
  selfSufficiencyByYear: OLAPTransformerResult | null;
  consumtionByYear: OLAPTransformerResult | null;
  orderedCountriesIds: number[] | null;
};

export function useChart1(data: TransformData): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();
  const { consumtionByYear, populationByYear, selfSufficiencyByYear, orderedCountriesIds } = data;

  const categories = useMemo(
    () =>
      populationByYear &&
      orderBy(populationByYear.columns, (c) => orderedCountriesIds?.indexOf(Number(c.selection[CATEGORIES_AXIS]))).map(
        (x) => x.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name || "",
      ),
    [populationByYear],
  );

  const getPointHint = (rowIndex: number, pointIndex: number) => {
    const year = consumtionByYear?.rows[rowIndex]?.attributes[`${SERIES_AXIS}.name`];
    const cropYear = yearToCropYearShort(year);

    const consumptionPoints = orderBy(consumtionByYear?.rows[rowIndex]?.points, (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });
    const consumption = {
      title: `${cropYear} Consumption per capita (kg/person)`,
      value: consumptionPoints[pointIndex]?.value || 0,
    };

    const populationPoints = populationByYear?.rows[rowIndex] && orderBy(populationByYear?.rows[rowIndex]?.points, (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });

    const population = populationPoints && {
      title: `${cropYear} Population (millions)`,
      value: populationPoints[pointIndex]?.value || 0,
    };

    const selfSufficiencyPoints = orderBy(selfSufficiencyByYear?.rows[rowIndex]?.points, (p) => {
      return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
    });
    const selfSufficiency = {
      title: `${cropYear} Self-sufficiency (%)`,
      value: selfSufficiencyPoints[pointIndex]?.value || 0,
    };

    return [population, selfSufficiency, consumption].filter(Boolean);
  };

  const options = useChartOptions({}, [
    chartPipe("column"),
    legendPipe("top", {
      labelFormatter: function() {
        const { name } = this;
        switch (name) {
          case "Production":
            return name + " (million mt)";
          case "Area Harvested":
            return name + " (million ha)";
          case "Self Sufficiency (%)":
            return name + " (mt/ha)";
          default:
            return name;
        }
      },
    }),
    addXAxisPipe(categories),
    addYAxisPipe({
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 0);
          }
          return label;
        },
      },
      title: {
        text: "Population (millions)",
      },
    }),
    addYAxisPipe({
      opposite: true,
      labels: {
        formatter: function() {
          var label = this.axis.defaultLabelFormatter.call(this);
          if (/^[0-9]{4}$/.test(label)) {
            return Highcharts.numberFormat(this.value, 2);
          }
          return label;
        },
      },
      title: {
        text: "Self Sufficiency (%)",
      },
    }),
    tooltipPipe((serie) => {
      switch (serie) {
        case "Production":
          return "million mt";
        case "Area Harvested":
          return "million ha";
        case "Self Sufficiency (%)":
          return "";
        default:
          return "";
      }
    }),
  ]);

  useEffect(() => {
    if (consumtionByYear && populationByYear && selfSufficiencyByYear) {
      const lastYearIndex = selfSufficiencyByYear.rows.length - 1;
      const selfSufficiencyLastYear = { ...selfSufficiencyByYear, rows: [selfSufficiencyByYear.rows[lastYearIndex]] };

      const columnSeries1 = populationByYear.rows.filter(Boolean).map<SeriesOptionsType>((row, i, arr) => {
        return {
          type: "column",
          yAxis: 0,
          showInLegend: true,
          dataLabels: {
            enabled: i === 0 || i === arr.length - 1,
            formatter: function() {
              return Highcharts.numberFormat(this.y || 0, 0);
            },
          },
          name: yearToCropYearShort(row.attributes[`${SERIES_AXIS}.name`]),
          id: row.attributes[`${SERIES_AXIS}.name`],
          data: orderBy(row.points, (p) => {
            return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
          }).map((p, pointIndex) => ({
            y: p.value,
            attributes: { ...p.attributes, highlightType: "row" },
            hintInfo: getPointHint(i, pointIndex),
          })),
        };
      });

      const colors = ["#B40000", "rgb(0, 88, 151)"];
      const columnSeries2 = selfSufficiencyLastYear.rows.filter(Boolean).map<SeriesOptionsType>((row, i) => {
        return {
          type: "spline",
          yAxis: 1,
          zIndex: 1,
          dashStyle: "Dash",
          color: colors[i],
          showInLegend: true,
          dataLabels: {
            color: "#B40000",
            enabled: true,
            formatter: function() {
              return Highcharts.numberFormat(this.y || 0, 1);
            },
          },
          lineWidth: 2,
          marker: {
            symbol: "diamond",
          },
          states: {
            hover: {
              lineWidth: 0,
              lineWidthPlus: 0,
            },
          },
          name: `Self Sufficiency (%) ${yearToCropYearShort(row.attributes[`${SERIES_AXIS}.name`])}`,
          id: row.attributes[`${SERIES_AXIS}.name`],
          data: orderBy(row.points, (p) => {
            return categories?.indexOf(p.column.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name ?? "");
          }).map((p, pointIndex) => ({
            y: p.value || 0,
            attributes: { ...p.attributes, highlightType: "cell" },
            hintInfo: getPointHint(lastYearIndex, pointIndex),
          })),
        };
      });

      const result: Highcharts.Options = {
        ...options,
        series: columnSeries1.concat(columnSeries2),
      };

      setState(result);
    }
  }, [consumtionByYear, populationByYear, selfSufficiencyByYear]);
  return state;
}
